import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import UserContextP from "Interfaces/Context"
import axios from "axios"

import {
    Button,
    Pagination,
    Stack,
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Chip,
    Grid,
    IconButton,
    Tooltip,
    Paper,
    InputAdornment,
    TextField,
    Autocomplete,
} from "@mui/material"

// Componentes
import Loader from "Components/Loader"
import ErrorPage from "Components/NotAccesible/ErrorPage"
import UserInformationModal from "../Admin/UserControl/UserInformationModal"

// Iconos
import NotData from "assets/images/Comercial/no-data.png"
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import { useAuth } from "Context/Auth/useAuth"

const UsersSupport = () => {
    const context = useContext(UserContextP)
    const auth = useAuth()

    const { t } = useTranslation()

    const [listUser, setListUser] = useState<any[]>([])
    const [copyListUser, setCopyListUser] = useState<any[]>([])
    const [error, setError] = useState(false)
    const [loader, setLoader] = useState(true)
    const [pagination, setPagination] = useState(1)
    const [openDetailsUser, setOpenDetailsUser] = useState(false)
    const [userSelected, setUserSelected] = useState(null)
    const [elementsToShow, setElementsToShow] = useState(12)

    const [listAllRoles, setListAllRoles] = useState<any[]>([])
    const [listAllCanals, setListAllCanals] = useState<any[]>([])

    const [searchText, setSearchText] = useState("")
    const [widthGrid, setWidthGrid] = useState(3)
    const [filterRole, setFilterRole] = useState<any>(null)
    const [filterVendorCode, setFilterVendorCode] = useState<any>(null)

    const loadingRef = useRef(false)

    const handleUserUpdated = (updatedUser: any) => {
        setCopyListUser((prevList) => {
            const updated = prevList.map((user) =>
                user.id === updatedUser.userKey || user.$key === updatedUser.userKey
                    ? { ...user, ...updatedUser }
                    : user
            )
            const updatedItem = updated.find((u) => u.id === updatedUser.userKey || u.$key === updatedUser.userKey)
            if (updatedItem && ((userSelected as any)?.id === updatedUser.userKey || (userSelected as any)?.$key === updatedUser.userKey)) {
                setUserSelected(updatedItem)
            }
            return updated
        })
    }

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1480 && window.innerWidth < 1800) setWidthGrid(4)
            else if (window.innerWidth >= 1300 && window.innerWidth < 1480) setWidthGrid(6)
            else if (window.innerWidth >= 900 && window.innerWidth < 1300) setWidthGrid(12)
            else if (window.innerWidth >= 680 && window.innerWidth < 900) setWidthGrid(6)
            else if (window.innerWidth < 680) setWidthGrid(12)
        }
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])



    useMemo(() => {
        if (window.innerWidth >= 1250) setElementsToShow(12)
        else if (window.innerWidth > 610 && window.innerWidth < 1250) setElementsToShow(9)
        else if (window.innerWidth <= 610) setElementsToShow(6)
    }, [])

    useEffect(() => {
        if (loader && !loadingRef.current) {
            loadingRef.current = true
            getData()
        }
        // eslint-disable-next-line
    }, [loader])

    // Filtrado de usuarios
    useEffect(() => {
        let filtered = copyListUser
        
        // Filtro por búsqueda de texto
        if (searchText !== "") {
            filtered = filtered.filter((user: any) => {
                const searchLower = searchText.toLowerCase()
                const name = (user?.name ?? "").toLowerCase()
                const lastName = (user?.last_name ?? "").toLowerCase()
                const email = (user?.email ?? "").toLowerCase()
                const roleName = (user?.roleName ?? "").toLowerCase()
                return (
                    name.includes(searchLower) ||
                    lastName.includes(searchLower) ||
                    email.includes(searchLower) ||
                    roleName.includes(searchLower)
                )
            })
        }
        
        // Filtro por rol
        if (filterRole) {
            filtered = filtered.filter((user: any) => user.role === filterRole.id)
        }
        
        // Filtro por canal de vendedor
        if (filterVendorCode) {
            filtered = filtered.filter((user: any) => {
                const vendorCodes = String(user?.vendor_code ?? "").split(",").map((c: string) => c.trim())
                return vendorCodes.includes(filterVendorCode.id)
            })
        }
        
        setListUser(filtered)
        setPagination(1)
    }, [searchText, filterRole, filterVendorCode, copyListUser])

    const getData = async () => {
        try {
            let body: any = {
                token: auth.user?.id,
                params: []
            }
            const headers = {
                "content-type": "application/json",
                "Authorization": `UserToken ${auth.auth?.token}`
            }
            let listRequest = [
                {
                    url: context.service.router.POST.get_list_user,
                    method: "POST",
                    headers,
                    body: body,
                    callBack: (response: any) => {
                        setCopyListUser(response.data.data)
                        setLoader(false)
                    },
                    error: (_: any) => {
                        setError(true)
                        setLoader(false)
                    }
                }, {
                    url: context.service.router.GET.get_list_roles,
                    method: "GET",
                    headers,
                    body: {},
                    callBack: (response: any) => {
                        setListAllRoles(response.data.data)
                    },
                    error: (_: any) => {
                        setError(true)
                        setLoader(false)
                    }
                }, {
                    url: context.service.router.GET.all_canals,
                    method: "GET",
                    headers,
                    body: {},
                    callBack: (response: any) => {
                        setListAllCanals(response.data.data)
                    },
                    error: (_: any) => {
                        setError(true)
                        setLoader(false)
                    }
                }
            ]
            let promises: any = []

            if (auth.user?.role === "adminsupport") body.params = []
            else if (auth.user?.role === "admin" || auth.user?.role === "adminComercial") body.params = ["vendor", "displayer", "userStore", "buyer", "displayerSupervisor", "cellarmanager", "delivery"]
            else if (auth.user?.role === "displayerSupervisor") body.params = ["displayer"]

            listRequest.forEach((item: any) => {
                promises.push(axios({
                    method: item.method,
                    url: item.url,
                    headers: item.headers,
                    data: item.body
                }).then(item.callBack).catch(item.error))
            })
            await Promise.all(promises)
        } finally {
            loadingRef.current = false
        }
    }

    const paginatedUsers = useMemo(() => {
        const start = elementsToShow * (pagination - 1)
        const end = elementsToShow * pagination
        return listUser.slice(start, end)
    }, [listUser, pagination, elementsToShow])

    return (
        <>
            <UserInformationModal
                open={openDetailsUser}
                roleList={listAllRoles}
                setOpen={setOpenDetailsUser}
                setReLoad={setLoader}
                data={userSelected}
                setData={setUserSelected}
                canalList={listAllCanals}
                urlCreate={context.service?.router?.CREATE?.account ?? ""}
                urlCredential={context.service?.router?.UPDATE?.user_credentials ?? ""}
                urlInformation={context.service?.router?.UPDATE?.user_information ?? ""}
                onUserUpdated={handleUserUpdated}
            />
            {
                loader ?
                    <Loader
                        inPage={true}
                        openLoader={loader}
                        redirect={false}
                        redirectHome={false}
                        quantity={0} />
                    :
                    error ?
                        <ErrorPage message={t("benchmarks.text_1")} />
                        :
                        <>
                            {
                                copyListUser.length > 0 ?
                                    <Box>
                                        {/* Header con búsqueda y botón */}
                                        <Box sx={{ mb: 3 }}>
                                            <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                                                <Grid item xs={12} md={7}>
                                                    <TextField
                                                        fullWidth
                                                        placeholder="Buscar por nombre, email o rol..."
                                                        value={searchText}
                                                        onChange={(e) => setSearchText(e.target.value)}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <SearchIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: 'white',
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={5}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="large"
                                                        disabled={loader}
                                                        startIcon={<AddIcon />}
                                                        onClick={() => {
                                                            setUserSelected(null)
                                                            setOpenDetailsUser(true)
                                                        }}
                                                        sx={{
                                                            py: 1.5,
                                                            fontWeight: 600,
                                                            backgroundColor: '#2e7d32',
                                                            '&:hover': {
                                                                backgroundColor: '#1b5e20'
                                                            }
                                                        }}
                                                    >
                                                        Crear Usuario
                                                    </Button>
                                                </Grid>
                                            </Grid>

                                            {/* Filtros */}
                                            {/* <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                        Filtrar por Rol
                                                    </Typography>
                                                    <Autocomplete
                                                        options={Array.isArray(listAllRoles) ? listAllRoles : []}
                                                        getOptionLabel={(option: any) => {
                                                            if (!option) return "";
                                                            return option?.label ?? "";
                                                        }}
                                                        value={filterRole ?? null}
                                                        onChange={(_, newValue) => setFilterRole(newValue)}
                                                        isOptionEqualToValue={(option: any, value: any) => {
                                                            if (!option || !value) return option === value;
                                                            return option?.id === value?.id;
                                                        }}
                                                        clearOnEscape
                                                        noOptionsText="Sin opciones"
                                                        renderInput={(params) => (
                                                            <TextField {...params} placeholder="Seleccionar rol..." size="small" />
                                                        )}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: 'white',
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                        Filtrar por Canal de Vendedor
                                                    </Typography>
                                                    <Autocomplete
                                                        options={Array.isArray(listAllCanals) ? listAllCanals : []}
                                                        getOptionLabel={(option: any) => {
                                                            if (!option) return "";
                                                            return option?.name ?? "";
                                                        }}
                                                        value={filterVendorCode ?? null}
                                                        onChange={(_, newValue) => setFilterVendorCode(newValue)}
                                                        isOptionEqualToValue={(option: any, value: any) => {
                                                            if (!option || !value) return option === value;
                                                            return option?.id === value?.id;
                                                        }}
                                                        clearOnEscape
                                                        noOptionsText="Sin opciones"
                                                        renderInput={(params) => (
                                                            <TextField {...params} placeholder="Seleccionar canal..." size="small" />
                                                        )}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: 'white',
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid> */}

                                            {/* Stats */}
                                            <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
                                                <Chip
                                                    icon={<GroupIcon />}
                                                    label={`${listUser.length} ${listUser.length === 1 ? 'usuario' : 'usuarios'}`}
                                                    color="primary"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                                {searchText && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Mostrando resultados para "{searchText}"
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Grid de usuarios */}
                                        <Grid container spacing={2}>
                                            {paginatedUsers.map((item: any, index: number) => {
                                                const isActive = !item.disabled
                                                return (
                                                    <Grid item xs={widthGrid} key={item.id || index}>
                                                        <Card
                                                            sx={{
                                                                height: '100%',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                border: '1px solid #e0e0e0',
                                                                '&:hover': {
                                                                    transform: 'translateY(-4px)',
                                                                    boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                                                                    borderColor: '#173456',
                                                                    '& .arrow-icon': {
                                                                        transform: 'translateX(4px)',
                                                                    }
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                if (!loader) {
                                                                    setUserSelected(item)
                                                                    setOpenDetailsUser(true)
                                                                }
                                                            }}
                                                        >
                                                            <CardContent sx={{ p: 2.5 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                                    {/* Avatar */}
                                                                    <Avatar
                                                                        sx={{
                                                                            width: 56,
                                                                            height: 56,
                                                                            backgroundColor: isActive ? '#173456' : '#9e9e9e',
                                                                            fontSize: '1.5rem',
                                                                            fontWeight: 600
                                                                        }}
                                                                    >
                                                                        {item.name?.charAt(0)}{item.last_name?.charAt(0)}
                                                                    </Avatar>

                                                                    {/* Info */}
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                            <Typography
                                                                                variant="subtitle1"
                                                                                sx={{
                                                                                    fontWeight: 700,
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                    flex: 1
                                                                                }}
                                                                            >
                                                                                {item.name} {item.last_name}
                                                                            </Typography>
                                                                            <Tooltip title={isActive ? "Usuario activo" : "Usuario inactivo"}>
                                                                                {isActive ? (
                                                                                    <CheckCircleIcon sx={{ fontSize: 20, color: '#2e7d32' }} />
                                                                                ) : (
                                                                                    <CancelIcon sx={{ fontSize: 20, color: '#d32f2f' }} />
                                                                                )}
                                                                            </Tooltip>
                                                                        </Box>

                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                                                            <EmailIcon sx={{ fontSize: 14, color: '#666' }} />
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                                sx={{
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap'
                                                                                }}
                                                                            >
                                                                                {item.email}
                                                                            </Typography>
                                                                        </Box>

                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                            <Chip
                                                                                icon={<BadgeIcon />}
                                                                                label={item.roleName}
                                                                                size="small"
                                                                                sx={{
                                                                                    backgroundColor: '#e3f2fd',
                                                                                    color: '#173456',
                                                                                    fontWeight: 600,
                                                                                    fontSize: '0.7rem',
                                                                                    height: 24
                                                                                }}
                                                                            />
                                                                            <IconButton
                                                                                size="small"
                                                                                className="arrow-icon"
                                                                                sx={{
                                                                                    transition: 'transform 0.3s ease',
                                                                                    color: '#173456'
                                                                                }}
                                                                            >
                                                                                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                )
                                            })}
                                        </Grid>

                                        {/* Paginación */}
                                        {listUser.length > elementsToShow && (
                                            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                                                <Stack spacing={2}>
                                                    <Pagination
                                                        count={Math.ceil(listUser.length / elementsToShow)}
                                                        page={pagination}
                                                        onChange={(_, value) => setPagination(value)}
                                                        color="primary"
                                                        size="large"
                                                        showFirstButton
                                                        showLastButton
                                                    />
                                                </Stack>
                                            </Box>
                                        )}
                                    </Box>
                                    :
                                    <Paper sx={{ p: 8, textAlign: 'center', backgroundColor: '#fafafa' }}>
                                        <img src={NotData} alt="empty" style={{ width: '200px', opacity: 0.5 }} />
                                        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                                            No hay usuarios para mostrar
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                            Crea tu primer usuario haciendo clic en el botón de arriba
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            disabled={loader}
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                setUserSelected(null)
                                                setOpenDetailsUser(true)
                                            }}
                                            sx={{ mt: 3 }}
                                        >
                                            Crear Usuario
                                        </Button>
                                    </Paper>
                            }
                        </>
            }
        </>
    )
}

export default UsersSupport